import { dataPortalClient, dataPortalRetryAfterMs } from './data-portal.client';
import type {
  DataPortalLogoutResponse,
  DataPortalRefreshResult,
  DataPortalUser,
} from '../auth/data-portal-auth.types';

function record(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new Error('Invalid data portal authentication response.');
  }
  return value as Record<string, unknown>;
}

function requiredString(
  value: unknown,
  field: string,
  maxLength: number,
): string {
  if (typeof value !== 'string' || !value || value.length > maxLength) {
    throw new Error(`Invalid ${field} in authentication response.`);
  }
  return value;
}

function optionalString(
  value: unknown,
  field: string,
  maxLength: number,
): string | undefined {
  if (value === undefined) return undefined;
  return requiredString(value, field, maxLength);
}

function stringArray(value: unknown, field: string): string[] {
  if (
    !Array.isArray(value) ||
    value.length > 256 ||
    value.some((item) => typeof item !== 'string' || !item || item.length > 255)
  ) {
    throw new Error(`Invalid ${field} in authentication response.`);
  }
  return [...value] as string[];
}

function dataPortalUser(value: unknown): DataPortalUser {
  const source = record(value);
  const result: DataPortalUser = {
    sub: requiredString(source.sub, 'sub', 255),
    roles: stringArray(source.roles, 'roles'),
    groups: stringArray(source.groups, 'groups'),
  };
  const username = optionalString(source.username, 'username', 255);
  const email = optionalString(source.email, 'email', 320);
  const name = optionalString(source.name, 'name', 512);
  if (username !== undefined) result.username = username;
  if (email !== undefined) result.email = email;
  if (name !== undefined) result.name = name;
  return result;
}

function logoutUrl(value: unknown): string | undefined {
  if (value === undefined) return undefined;
  const raw = requiredString(value, 'logoutUrl', 2_048);
  let parsed: URL;
  try {
    parsed = new URL(raw);
  } catch {
    throw new Error('Invalid logoutUrl in authentication response.');
  }
  if (
    !['https:', 'http:'].includes(parsed.protocol) ||
    parsed.username ||
    parsed.password
  ) {
    throw new Error('Unsafe logoutUrl in authentication response.');
  }
  return parsed.toString();
}

function dataPortalLogout(value: unknown): DataPortalLogoutResponse {
  const source = record(value);
  if (
    !['complete', 'partial'].includes(String(source.status)) ||
    source.localSessionCleared !== true ||
    typeof source.sessionTombstoned !== 'boolean' ||
    !(
      typeof source.refreshTokenRevoked === 'boolean' ||
      source.refreshTokenRevoked === null
    )
  ) {
    throw new Error('Invalid data portal logout response.');
  }
  const complete =
    source.sessionTombstoned && source.refreshTokenRevoked !== false;
  if ((source.status === 'complete') !== complete) {
    throw new Error('Inconsistent data portal logout response.');
  }
  return {
    status: source.status as 'complete' | 'partial',
    localSessionCleared: true,
    sessionTombstoned: source.sessionTombstoned,
    refreshTokenRevoked: source.refreshTokenRevoked,
    ...(source.logoutUrl === undefined
      ? {}
      : { logoutUrl: logoutUrl(source.logoutUrl) }),
  };
}

export const dataPortalAuthApi = {
  async me(): Promise<DataPortalUser> {
    const response = await dataPortalClient.get<unknown>('/auth/me');
    return dataPortalUser(response.data);
  },

  async refresh(): Promise<DataPortalRefreshResult> {
    const response = await dataPortalClient.post('/auth/refresh', undefined, {
      validateStatus: (status) =>
        (status >= 200 && status < 300) || status === 409,
    });
    return response.status === 409
      ? {
          status: 'conflict',
          retryAfterMs: dataPortalRetryAfterMs(response.headers['retry-after']),
        }
      : { status: 'refreshed' };
  },

  async logout(): Promise<DataPortalLogoutResponse> {
    const response = await dataPortalClient.post<unknown>(
      '/auth/logout',
      undefined,
      { validateStatus: () => true },
    );
    return dataPortalLogout(response.data);
  },
};
